import api from "./api";

const DEFAULT_INACTIVE_DAYS = 14;

// Repeat array params as `filter=a&filter=b` (what the Django endpoint reads),
// instead of axios' default `filter[]=a`.
export function serializeParams(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    (Array.isArray(value) ? value : [value]).forEach((item) => search.append(key, item));
  });
  return search.toString();
}

function analyticsConfig(
  {
    start_date,
    end_date,
    territory_id,
    comparison,
    comparison_start_date,
    comparison_end_date,
    filters,
    inactive_days,
  },
  options = {}
) {
  const params = { start_date, end_date };
  const optionalParams = {
    territory_id,
    comparison,
    comparison_start_date,
    comparison_end_date,
  };

  Object.entries(optionalParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      params[key] = value;
    }
  });
  if (inactive_days != null && inactive_days !== DEFAULT_INACTIVE_DAYS) {
    params.inactive_days = inactive_days;
  }

  const config = { params };
  if (Array.isArray(filters) && filters.length) {
    params.filter = [...filters];
    config.paramsSerializer = { serialize: serializeParams };
  }
  if (options.signal) {
    config.signal = options.signal;
  }
  return config;
}

// The Summary highlights and the Customers / Products / Salesmen tabs ask for the same
// side reports; identical requests within a couple of minutes share one response.
const CACHE_MS = 2 * 60 * 1000;
const cache = new Map();

export function clearAnalyticsCache() {
  cache.clear();
}

function canceled() {
  return Object.assign(new Error("canceled"), { name: "CanceledError", code: "ERR_CANCELED" });
}

// Lets one caller stop waiting without cancelling the shared request for the others.
function untilAborted(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(canceled());
  return new Promise((resolve, reject) => {
    const abort = () => reject(canceled());
    signal.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => { signal.removeEventListener("abort", abort); resolve(value); },
      (error) => { signal.removeEventListener("abort", abort); reject(error); },
    );
  });
}

function cachedGet(url, config, signal) {
  const key = `${url}?${serializeParams(config.params || {})}`;
  const now = Date.now();
  let entry = cache.get(key);
  if (!entry || now - entry.time > CACHE_MS) {
    const promise = api.get(url, config).then((response) => response.data);
    entry = { time: now, promise };
    cache.set(key, entry);
    // A failed request is not kept, so Retry asks the server again.
    promise.catch(() => { if (cache.get(key) === entry) cache.delete(key); });
  }
  return untilAborted(entry.promise, signal);
}

export async function getSalesAnalytics(request, options = {}) {
  const response = await api.get("/api/accounting/sales_analytics/", analyticsConfig(request, options));
  return response.data;
}

// Read-only: margin, reach, reorder rate, momentum and untapped customers per product.
export function getProductOpportunities(request, options = {}) {
  const { inactive_days, ...rest } = request;
  return cachedGet("/api/accounting/sales_analytics/products/", analyticsConfig(rest), options.signal);
}

// Read-only: customers behind their own usual buying pattern.
export function getCustomerHealth({ as_of, territory_id, filters, min_weekly }, options = {}) {
  const params = {};
  if (as_of) params.as_of = as_of;
  if (territory_id != null && territory_id !== "") params.territory_id = territory_id;
  if (min_weekly != null && min_weekly !== "") params.min_weekly = min_weekly;
  const config = { params };
  if (Array.isArray(filters) && filters.length) {
    params.filter = [...filters];
    config.paramsSerializer = { serialize: serializeParams };
  }
  return cachedGet("/api/accounting/sales_analytics/customer_health/", config, options.signal);
}

// Read-only: per-salesman results, book coverage, retention and task follow-through.
export function getSalesmanScorecard(request, options = {}) {
  const { inactive_days, filters, ...rest } = request;
  return cachedGet("/api/accounting/sales_analytics/salesmen/", analyticsConfig(rest), options.signal);
}

// Read-only: this Yom Tov season against last season, aligned by Hebrew date.
export async function getHolidaySeason({ season, year, lead_weeks, territory_id, filters }, options = {}) {
  const params = {};
  if (season) params.season = season;
  if (year) params.year = year;
  if (lead_weeks) params.lead_weeks = lead_weeks;
  if (territory_id != null && territory_id !== "") params.territory_id = territory_id;
  const config = { params };
  if (Array.isArray(filters) && filters.length) {
    params.filter = [...filters];
    config.paramsSerializer = { serialize: serializeParams };
  }
  if (options.signal) config.signal = options.signal;
  const response = await api.get("/api/accounting/sales_analytics/season/", config);
  return response.data;
}

// Read-only: one customer at a glance for a call (pattern, what changed, peers, tasks, contacts).
export function getCustomerDetail({ key, as_of }, options = {}) {
  const params = { key };
  if (as_of) params.as_of = as_of;
  return cachedGet("/api/accounting/sales_analytics/customer/", { params }, options.signal);
}
